import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import ProductImage from '../../../components/productImage';
import { formatCurrency } from '../../../utils/format.js';
import { AddOrderDeliveryContext } from './AddOrderDeliveryContext';
import ModalSelectProduct from './ModalSelectDeliveryProduct';

const { confirm } = Modal;
const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 280;

/** Салбарын select-ийн өргөн — картын өргөнөөр сунахгүй, баруун талд зай үлдэнэ */
const SELECT_WIDTH = 260;

/** Тоог тооцоололд оруулахад NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Branch = ({ id, products = [], form }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [branchCode, setBranchCode] = useState('');

  const addContext = useContext(AddOrderDeliveryContext);

  const branches = useMemo(() => {
    // ЗӨВХӨН бусад блок дээр сонгогдсон салбаруудыг сонголтоос хасна
    const takenByOtherBlocks = (addContext.selectedBranches ?? [])
      .filter((item) => item.id !== id)
      .map((item) => item.value);

    const reference = addContext?.deliveryReference?.branches;
    return {
      types: reference?.types?.filter((type) => !takenByOtherBlocks.includes(type)),
      detail: reference?.detail,
    };
  }, [addContext.selectedBranches, addContext?.deliveryReference, id]);

  // Draft-аас сэргэсэн утгыг формоос локал state рүү авна
  useEffect(() => {
    setBranchCode(form.getFieldValue(`br-code-${id}`));
    //eslint-disable-next-line
  }, []);

  const onChangeBranch = (value) => {
    setBranchCode(value);
    addContext.addSelectedBranches({ id, value });
  };

  const onClickSelectProduct = () => {
    if (!branchCode) {
      message.error('Бараа татах салбараа сонгоно уу!');
      return;
    }
    setIsModalVisible(true);
  };

  const removeProduct = (record) =>
    confirm({
      title: 'Барааг устгах уу?',
      okText: 'Устгах',
      cancelText: 'Цуцлах',
      okButtonProps: { danger: true },
      onOk: () => {
        form.setFieldsValue(form.getFieldsValue());
        // Өмнө нь агуулахын жагсаалтаас устгахыг оролддог байсан
        addContext.removeProductBranch(id, record._id);
      },
    });

  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage
            src={record?.imageCover}
            alt={record?.title ?? 'Барааны зураг'}
            size={IMAGE_SIZE}
            style={{
              objectFit: 'contain',
              borderRadius: 4,
              border: '1px solid #eaecf0',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
            }}
          />
          <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
            <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.title }}>
              {record?.title ?? '-'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record?.productSku}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      key: 'price',
      title: 'Нэгжийн үнэ',
      align: 'right',
      width: 140,
      render: (_, record) => formatCurrency(num(record?.price)),
    },
    {
      key: 'availableStock',
      title: 'Үлдэгдэл',
      align: 'right',
      width: 110,
      render: (_, record) => (record?.availableStock != null ? `${record.availableStock} ш` : '-'),
    },
    {
      key: 'totalUnit',
      title: 'Тоо ширхэг',
      align: 'right',
      width: 160,
      render: (_, record, index) => (
        <Form.Item
          name={[`br-product-${id}`, `${record._id}`, 'totalUnit']}
          style={{ marginBottom: 0 }}
          rules={[
            { required: true, message: 'Тоо ширхэг оруулна уу!' },
            { pattern: new RegExp(/^[0-9]+$/), message: 'Бүхэл тоо оруулна уу!' },
          ]}
        >
          <InputNumber
            placeholder="0"
            addonAfter="ш"
            style={{ width: '100%' }}
            min={1}
            onChange={(value) => addContext.onChangeProductUnitBranch(id, index, value)}
          />
        </Form.Item>
      ),
    },
    {
      key: 'total',
      title: 'Нийт үнэ',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <Text strong>{formatCurrency(num(record?.price) * num(record?.totalUnit))}</Text>
      ),
    },
    {
      key: 'action',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Устгах">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeProduct(record)}
          />
        </Tooltip>
      ),
    },
  ];

  /** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт үнэ */
  const renderSummary = (rows = []) => {
    const totalUnit = rows.reduce((sum, row) => sum + num(row?.totalUnit), 0);
    const totalPrice = rows.reduce((sum, row) => sum + num(row?.totalUnit) * num(row?.price), 0);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={4} align="right">
          <Text type="secondary">Нийт</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <Text strong>{`${totalUnit} ш`}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">
          <Text strong>{formatCurrency(totalPrice)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} />
      </Table.Summary.Row>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[16, 8]} align="bottom">
        {/* Select нь дэлгэцийн хэмжээнээс үл хамааран богино — үлдсэн зай нь чөлөөтэй */}
        <Col flex={`0 0 ${SELECT_WIDTH}px`} style={{ maxWidth: '100%' }}>
          <Form.Item
            label="Солих барааг татах салбар"
            name={`br-code-${id}`}
            rules={[{ required: true, message: 'Бараа татах салбараа сонгоно уу!' }]}
            style={{ marginBottom: 0 }}
          >
            <Select placeholder="Салбар сонгох" onChange={onChangeBranch}>
              {branches?.types?.map((type) => (
                <Select.Option value={type} key={type}>
                  {branches?.detail?.[type]?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col flex="auto">
          <Button type="primary" icon={<PlusOutlined />} onClick={onClickSelectProduct}>
            Бараа нэмэх
          </Button>
        </Col>
      </Row>

      {products?.length ? (
        <Table
          columns={columns}
          dataSource={products}
          rowKey={(record, index) => record?._id ?? index}
          size="middle"
          pagination={false}
          summary={renderSummary}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text type="secondary" style={{ fontSize: 13 }}>
              {branchCode
                ? '"Бараа нэмэх" товчоор бараагаа сонгоно уу.'
                : 'Эхлээд бараа татах салбараа сонгоно уу.'}
            </Text>
          }
          style={{ margin: 0, padding: '24px 0' }}
        />
      )}

      {isModalVisible && (
        <ModalSelectProduct
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          selectedProducts={products}
          branchCode={branchCode}
          id={id}
        />
      )}
    </Space>
  );
};

export default Branch;
